import { useState } from 'react'
import type { StoredLayout } from '../../lib/api'
import type { ProfileUnit, UnitSettings } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { Field, Num, Text } from '../parts'
import { Confirm } from '../sectionKit'
import { SetupModal } from '../setupKit'
import { SettingsForm } from './SettingsForm'

/** One profile unit: name, default count and its kind's settings. Applies to the tab's local document. */
export function UnitSheet({
  unit,
  units,
  inherited,
  choices,
  readOnly,
  onClose,
  onApply,
  onDelete,
}: {
  unit: ProfileUnit
  /** Every unit of the document, for the name check. */
  units: ProfileUnit[]
  /** The kind's built-in settings. */
  inherited: UnitSettings
  choices: { screen: StoredLayout[]; ticket: StoredLayout[] }
  readOnly: boolean
  onClose: () => void
  onApply: (next: ProfileUnit) => void
  onDelete: () => void
}) {
  const { say } = useStaffText()
  const [name, setName] = useState(unit.name)
  const [count, setCount] = useState(unit.defaultCount)
  const [settings, setSettings] = useState<UnitSettings>(unit.settings ?? {})
  const key = name.trim().toLowerCase()
  const taken = key !== '' && units.some((u) => u.id !== unit.id && u.name.trim().toLowerCase() === key)

  return (
    <SetupModal
      wide
      title={unit.name}
      onClose={onClose}
      onSubmit={() => onApply({ ...unit, name: name.trim(), defaultCount: count, settings })}
      submitLabel={say('eq.tab.done')}
      valid={key !== '' && !taken && Number.isInteger(count) && count >= 0 && count <= 99}
      hideSubmit={readOnly}
      extra={
        !readOnly && (
          <Confirm
            ask={say('common.delete')}
            question={say('eq.sheet.removeAsk')}
            yes={say('common.delete')}
            keep={say('common.keep')}
            onYes={onDelete}
          />
        )
      }
    >
      <div className="admin-form">
        <Field label={say('common.name')}>
          <Text value={name} onChange={setName} disabled={readOnly} required />
        </Field>
        {taken && <p className="field-error">{say('eq.sheet.nameTaken')}</p>}
        <Field label={say('eq.tab.count')}>
          <Num value={count} onChange={setCount} min={0} max={99} disabled={readOnly} />
        </Field>
      </div>
      <SettingsForm
        kind={unit.kind}
        own={settings}
        inherited={inherited}
        inheritedFrom="default"
        choices={choices}
        onChange={setSettings}
        readOnly={readOnly}
      />
    </SetupModal>
  )
}
